import { useCallback, useEffect, useMemo, useState } from 'react';
import { isApiError } from '@aiptyg/types';
import * as settingsService from '../services/settings.service';
import type {
  WithdrawalPayoutChannel,
  WithdrawalPayoutAmountDTO,
  WithdrawalPayoutConfigDTO,
  WithdrawalPayoutConfigUpdateParams,
  WithdrawalPayoutFixedAmountDTO,
  WithdrawalPayoutMode,
  WithdrawalPayoutProvider,
  WithdrawalPayoutProviderConfigDTO,
} from '../types/settings.types';
import { useToastStore } from '../../../store/toast.store';

function resolveError(e: unknown): string {
  if (isApiError(e)) {
    const map: Record<string, string> = {
      admin_role_required: '无管理员权限',
      network_error: '网络连接失败，请检查网络',
      'invalid withdrawal payout config': '自动打款配置无效，请检查接口凭据、自动模式、通道开关和回调路径',
    };
    return map[e.message] ?? `请求失败 (${e.message})`;
  }
  return '未知错误';
}

type WithdrawalPayoutForm = WithdrawalPayoutConfigUpdateParams;
type FixedAmountChannelValue = 'none' | 'sypaymon' | 'alipay_direct' | `alipay_direct:${string}`;

const emptyProviderConfig = (notifyPath: string): WithdrawalPayoutProviderConfigDTO => ({
  id: '',
  name: '',
  enabled: false,
  app_id: '',
  secret: '',
  base_url: '',
  notify_path: notifyPath,
  alipay_product_id: 0,
  alipay_amounts: [],
  alipay_public_key: '',
  alipay_app_cert: '',
  alipay_public_cert: '',
  alipay_root_cert: '',
  merchant_private_key: '',
  platform_public_key: '',
});

const EMPTY_FORM: WithdrawalPayoutForm = {
  enabled: true,
  auto_review_enabled: true,
  provider: 'sypaymon',
  sypaymon: emptyProviderConfig('/v1/webhooks/sypaymon/payout'),
  alipay_direct_config: emptyProviderConfig('/v1/webhooks/alipay/payout'),
  alipay_direct_accounts: [],
  csjvnpay: emptyProviderConfig('/v1/webhooks/csjvnpay/payout'),
  app_id: '',
  secret: '',
  base_url: '',
  alipay_public_key: '',
  alipay_app_cert: '',
  alipay_public_cert: '',
  alipay_root_cert: '',
  default_mode: 'manual',
  auto_channels: ['alipay'],
  max_auto_amount_fen: 500000,
  retry_limit: 3,
  query_interval_seconds: 120,
  bank_card_enabled: false,
  alipay_enabled: true,
  alipay_product_id: 0,
  alipay_amounts: [],
  fixed_amounts: [],
  bank_card_product_id: 0,
  notify_path: '/v1/webhooks/sypaymon/payout',
  allow_user_ids: [],
  deny_user_ids: [],
};

function dtoToForm(dto: WithdrawalPayoutConfigDTO): WithdrawalPayoutForm {
  const sypaymonAmounts = dto.sypaymon?.alipay_amounts ?? [];
  const directAmounts = dto.alipay_direct_config?.alipay_amounts ?? [];
  const fixedAmounts = dto.fixed_amounts?.length
    ? dto.fixed_amounts
    : mergeLegacyProviderAmounts(sypaymonAmounts, directAmounts);
  return {
    enabled: true,
    auto_review_enabled: dto.auto_review_enabled,
    provider: dto.provider,
    sypaymon: {
      ...emptyProviderConfig('/v1/webhooks/sypaymon/payout'),
      ...(dto.sypaymon ?? {}),
      secret: '',
      alipay_public_key: '',
      alipay_app_cert: '',
      alipay_public_cert: '',
      alipay_root_cert: '',
      alipay_amounts: sypaymonAmounts,
    },
    alipay_direct_config: {
      ...emptyProviderConfig('/v1/webhooks/alipay/payout'),
      ...(dto.alipay_direct_config ?? {}),
      secret: '',
      alipay_public_key: '',
      alipay_app_cert: '',
      alipay_public_cert: '',
      alipay_root_cert: '',
      alipay_amounts: directAmounts,
    },
    csjvnpay: {
      ...emptyProviderConfig('/v1/webhooks/csjvnpay/payout'),
      ...(dto.csjvnpay ?? {}),
      secret: '',
      merchant_private_key: '',
      platform_public_key: '',
    },
    alipay_direct_accounts: (dto.alipay_direct_accounts ?? []).map((account) => ({
      ...emptyProviderConfig('/v1/webhooks/alipay/payout'),
      ...account,
      id: account.id ?? '',
      name: account.name ?? '',
      secret: '',
      alipay_public_key: '',
      alipay_app_cert: '',
      alipay_public_cert: '',
      alipay_root_cert: '',
      alipay_amounts: account.alipay_amounts ?? [],
    })),
    app_id: dto.app_id ?? '',
    secret: '',
    base_url: dto.base_url ?? '',
    alipay_public_key: '',
    alipay_app_cert: '',
    alipay_public_cert: '',
    alipay_root_cert: '',
    default_mode: dto.default_mode,
    auto_channels: dto.auto_channels,
    max_auto_amount_fen: dto.max_auto_amount_fen,
    retry_limit: dto.retry_limit,
    query_interval_seconds: dto.query_interval_seconds,
    bank_card_enabled: dto.bank_card_enabled,
    alipay_enabled: dto.alipay_enabled,
    alipay_product_id: dto.alipay_product_id,
    alipay_amounts: dto.alipay_amounts ?? [],
    fixed_amounts: fixedAmounts,
    bank_card_product_id: dto.bank_card_product_id,
    notify_path: dto.notify_path,
    allow_user_ids: dto.allow_user_ids,
    deny_user_ids: dto.deny_user_ids,
  };
}

function mergeLegacyProviderAmounts(
  sypaymonAmounts: WithdrawalPayoutAmountDTO[],
  directAmounts: WithdrawalPayoutAmountDTO[],
): WithdrawalPayoutFixedAmountDTO[] {
  const rows = new Map<number, WithdrawalPayoutFixedAmountDTO>();
  const append = (provider: WithdrawalPayoutProvider, items: WithdrawalPayoutAmountDTO[]) => {
    items.forEach((item) => {
      const amountFen = Number(item.amount_fen) || 0;
      if (amountFen <= 0) return;
      const existing = rows.get(amountFen) ?? {
        amount_fen: amountFen,
        amount_yuan: item.amount_yuan,
        label: item.label || '',
        enabled: Boolean(item.enabled),
        sort_order: Number(item.sort_order) || rows.size + 1,
        channels: [],
      };
      existing.enabled = existing.enabled || Boolean(item.enabled);
      if (!existing.label && item.label) existing.label = item.label;
      existing.channels.push({
        provider,
        account_id: '',
        channel: 'alipay',
        enabled: Boolean(item.enabled),
        sort_order: provider === 'sypaymon' ? 1 : 2,
      });
      rows.set(amountFen, existing);
    });
  };
  append('sypaymon', sypaymonAmounts);
  append('alipay_direct', directAmounts);
  return Array.from(rows.values()).sort((left, right) => (
    left.sort_order === right.sort_order ? left.amount_fen - right.amount_fen : left.sort_order - right.sort_order
  ));
}

function userIDsToText(values: number[]): string {
  return values.map(String).join('\n');
}

function parseUserIDText(text: string): { values: number[]; invalidTokens: string[] } {
  const tokens = text
    .split(/[\s,，]+/)
    .map((value) => value.trim())
    .filter(Boolean);

  const values: number[] = [];
  const invalidTokens: string[] = [];
  const seen = new Set<number>();

  for (const token of tokens) {
    if (!/^\d+$/.test(token)) {
      invalidTokens.push(token);
      continue;
    }
    const userID = Number(token);
    if (!Number.isSafeInteger(userID) || userID <= 0) {
      invalidTokens.push(token);
      continue;
    }
    if (seen.has(userID)) {
      continue;
    }
    seen.add(userID);
    values.push(userID);
  }

  values.sort((a, b) => a - b);
  return { values, invalidTokens };
}

function isValidOptionalURL(value: string): boolean {
  return value === '' || value.startsWith('http://') || value.startsWith('https://');
}

function normalizeProviderConfigForSave(config: WithdrawalPayoutProviderConfigDTO): WithdrawalPayoutProviderConfigDTO {
  return {
    ...config,
    id: (config.id ?? '').trim().toLowerCase(),
    name: (config.name ?? '').trim(),
    app_id: config.app_id.trim(),
    secret: config.secret.trim(),
    base_url: config.base_url.trim().replace(/\/+$/, ''),
    notify_path: config.notify_path.trim(),
    alipay_product_id: Number(config.alipay_product_id) || 0,
    alipay_public_key: config.alipay_public_key.trim(),
    alipay_app_cert: config.alipay_app_cert.trim(),
    alipay_public_cert: config.alipay_public_cert.trim(),
    alipay_root_cert: config.alipay_root_cert.trim(),
    merchant_private_key: (config.merchant_private_key ?? '').trim(),
    platform_public_key: (config.platform_public_key ?? '').trim(),
    alipay_amounts: config.alipay_amounts
      .map((item) => ({
        ...item,
        amount_fen: Number(item.amount_fen) || 0,
        label: item.label.trim(),
        sort_order: Number(item.sort_order) || 0,
      }))
      .filter((item) => item.amount_fen > 0),
  };
}

function normalizeAlipayDirectAccountsForSave(items: WithdrawalPayoutProviderConfigDTO[]): WithdrawalPayoutProviderConfigDTO[] {
  const seen = new Set<string>();
  return items
    .map((item) => normalizeProviderConfigForSave(item))
    .filter((item) => {
      const id = item.id ?? '';
      if (!id || seen.has(id)) return false;
      seen.add(id);
      return true;
    });
}

function normalizeFixedAmountsForSave(items: WithdrawalPayoutFixedAmountDTO[]): WithdrawalPayoutFixedAmountDTO[] {
  return items
    .map((item, index) => ({
      ...item,
      amount_fen: Number(item.amount_fen) || 0,
      label: item.label.trim(),
      sort_order: Number(item.sort_order) || index + 1,
      channels: item.channels
        .map((channel) => ({
          ...channel,
          account_id: (channel.account_id ?? '').trim().toLowerCase(),
          channel: 'alipay' as const,
          sort_order: Number(channel.sort_order) || fixedAmountChannelSortOrder(channel),
        }))
        .filter((channel) => channel.provider === 'sypaymon' || channel.provider === 'alipay_direct'),
    }))
    .filter((item) => item.amount_fen > 0);
}

function fixedAmountChannelSortOrder(channel: { provider: WithdrawalPayoutProvider; account_id?: string }): number {
  if (channel.provider === 'sypaymon') return 1;
  return channel.account_id ? 3 : 2;
}

function defaultFixedAmountChannels(form: WithdrawalPayoutForm): WithdrawalPayoutFixedAmountDTO['channels'] {
  const channels: WithdrawalPayoutFixedAmountDTO['channels'] = [
    { provider: 'sypaymon', account_id: '', channel: 'alipay', enabled: false, sort_order: 1 },
    { provider: 'alipay_direct', account_id: '', channel: 'alipay', enabled: false, sort_order: 2 },
  ];
  form.alipay_direct_accounts.forEach((account, index) => {
    const id = (account.id ?? '').trim().toLowerCase();
    if (!id) return;
    channels.push({ provider: 'alipay_direct', account_id: id, channel: 'alipay', enabled: false, sort_order: 3 + index });
  });
  if (form.sypaymon.enabled) {
    channels[0] = { ...channels[0], enabled: true };
  } else if (form.alipay_direct_config.enabled) {
    channels[1] = { ...channels[1], enabled: true };
  } else {
    const firstEnabledAccountIndex = form.alipay_direct_accounts.findIndex((account) => account.enabled && (account.id ?? '').trim() !== '');
    if (firstEnabledAccountIndex >= 0) {
      channels[2 + firstEnabledAccountIndex] = { ...channels[2 + firstEnabledAccountIndex], enabled: true };
    }
  }
  return channels;
}

function selectFixedAmountChannel(
  channels: WithdrawalPayoutFixedAmountDTO['channels'],
  value: FixedAmountChannelValue,
): WithdrawalPayoutFixedAmountDTO['channels'] {
  const [provider, accountID = ''] = value === 'none' ? ['', ''] : value.split(':');
  const normalizedAccountID = accountID.trim().toLowerCase();
  let found = false;
  const nextChannels = channels.map((channel) => {
    const match = channel.provider === provider && (channel.account_id ?? '') === normalizedAccountID;
    if (match) found = true;
    return { ...channel, account_id: channel.account_id ?? '', enabled: match };
  });
  if (!found && provider === 'sypaymon') {
    nextChannels.push({ provider: 'sypaymon', account_id: '', channel: 'alipay', enabled: true, sort_order: 1 });
  }
  if (!found && provider === 'alipay_direct') {
    nextChannels.push({ provider: 'alipay_direct', account_id: normalizedAccountID, channel: 'alipay', enabled: true, sort_order: normalizedAccountID ? 3 : 2 });
  }
  return nextChannels;
}

function providerAmountsFromFixedAmounts(
  items: WithdrawalPayoutFixedAmountDTO[],
  provider: WithdrawalPayoutProvider,
): WithdrawalPayoutAmountDTO[] {
  return items
    .filter((item) => item.channels.some((channel) => channel.provider === provider && channel.channel === 'alipay'))
    .map((item) => ({
      amount_fen: item.amount_fen,
      amount_yuan: item.amount_yuan,
      label: item.label,
      enabled: item.enabled && item.channels.some((channel) => channel.provider === provider && channel.channel === 'alipay' && channel.enabled),
      sort_order: item.sort_order,
    }));
}

export function useWithdrawalPayoutConfig() {
  const addToast = useToastStore((s) => s.addToast);
  const [data, setData] = useState<WithdrawalPayoutConfigDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<WithdrawalPayoutForm>(EMPTY_FORM);
  const [allowUserIDsText, setAllowUserIDsText] = useState('');
  const [denyUserIDsText, setDenyUserIDsText] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await settingsService.getWithdrawalPayoutConfig();
      setData(result);
      const nextForm = dtoToForm(result);
      setForm(nextForm);
      setAllowUserIDsText(userIDsToText(nextForm.allow_user_ids));
      setDenyUserIDsText(userIDsToText(nextForm.deny_user_ids));
    } catch (e) {
      setError(resolveError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const updateField = useCallback(<K extends keyof WithdrawalPayoutForm>(key: K, value: WithdrawalPayoutForm[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  }, []);

  const updateProviderField = useCallback((
    provider: WithdrawalPayoutProvider,
    key: keyof WithdrawalPayoutProviderConfigDTO,
    value: WithdrawalPayoutProviderConfigDTO[keyof WithdrawalPayoutProviderConfigDTO],
  ) => {
    const formKey = provider === 'alipay_direct' ? 'alipay_direct_config' : provider === 'csjvnpay' ? 'csjvnpay' : 'sypaymon';
    setForm((prev) => ({
      ...prev,
      [formKey]: {
        ...prev[formKey],
        [key]: value,
      },
    }));
  }, []);

  const addAlipayDirectAccount = useCallback(() => {
    setForm((prev) => ({
      ...prev,
      alipay_direct_accounts: [
        ...prev.alipay_direct_accounts,
        {
          ...emptyProviderConfig('/v1/webhooks/alipay/payout'),
          id: `acct_${prev.alipay_direct_accounts.length + 1}`,
          name: `支付宝账号${prev.alipay_direct_accounts.length + 1}`,
          enabled: true,
        },
      ],
    }));
  }, []);

  const updateAlipayDirectAccount = useCallback((
    index: number,
    key: keyof WithdrawalPayoutProviderConfigDTO,
    value: WithdrawalPayoutProviderConfigDTO[keyof WithdrawalPayoutProviderConfigDTO],
  ) => {
    setForm((prev) => ({
      ...prev,
      alipay_direct_accounts: prev.alipay_direct_accounts.map((account, accountIndex) => (
        accountIndex === index ? { ...account, [key]: value } : account
      )),
    }));
  }, []);

  const removeAlipayDirectAccount = useCallback((index: number) => {
    setForm((prev) => ({
      ...prev,
      alipay_direct_accounts: prev.alipay_direct_accounts.filter((_, accountIndex) => accountIndex !== index),
    }));
  }, []);

  const setProvider = useCallback((provider: WithdrawalPayoutProvider) => {
    setForm((prev) => ({
      ...prev,
      provider,
    }));
  }, []);

  const setDefaultMode = useCallback((defaultMode: WithdrawalPayoutMode) => {
    setForm((prev) => ({ ...prev, default_mode: defaultMode }));
  }, []);

  const setChannelEnabled = useCallback((channel: WithdrawalPayoutChannel, enabled: boolean) => {
    setForm((prev) => {
      const next = { ...prev };
      if (channel === 'alipay') {
        next.alipay_enabled = enabled;
      } else {
        next.bank_card_enabled = enabled;
      }
      if (!enabled) {
        next.auto_channels = next.auto_channels.filter((value) => value !== channel);
      }
      return next;
    });
  }, []);

  const toggleAutoChannel = useCallback((channel: WithdrawalPayoutChannel, checked: boolean) => {
    setForm((prev) => {
      const nextChannels = checked
        ? Array.from(new Set([...prev.auto_channels, channel]))
        : prev.auto_channels.filter((value) => value !== channel);
      return {
        ...prev,
        auto_channels: nextChannels,
      };
    });
  }, []);

  const addFixedAmount = useCallback(() => {
    setForm((prev) => ({
      ...prev,
      fixed_amounts: [
        ...prev.fixed_amounts,
        {
          amount_fen: 0,
          label: '',
          enabled: true,
          sort_order: prev.fixed_amounts.length + 1,
          channels: defaultFixedAmountChannels(prev),
        },
      ],
    }));
  }, []);

  const updateFixedAmount = useCallback(<K extends keyof WithdrawalPayoutForm['fixed_amounts'][number]>(
    index: number,
    key: K,
    value: WithdrawalPayoutForm['fixed_amounts'][number][K],
  ) => {
    setForm((prev) => ({
      ...prev,
      fixed_amounts: prev.fixed_amounts.map((item, itemIndex) => (
          itemIndex === index ? { ...item, [key]: value } : item
      )),
    }));
  }, []);

  const removeFixedAmount = useCallback((index: number) => {
    setForm((prev) => ({
      ...prev,
      fixed_amounts: prev.fixed_amounts.filter((_, itemIndex) => itemIndex !== index),
    }));
  }, []);

  const setFixedAmountChannel = useCallback((index: number, value: FixedAmountChannelValue) => {
    setForm((prev) => ({
      ...prev,
      fixed_amounts: prev.fixed_amounts.map((item, itemIndex) => {
        if (itemIndex !== index) return item;
        return { ...item, channels: selectFixedAmountChannel(item.channels, value) };
      }),
    }));
  }, []);

  const parsedAllowUserIDs = useMemo(() => parseUserIDText(allowUserIDsText), [allowUserIDsText]);
  const parsedDenyUserIDs = useMemo(() => parseUserIDText(denyUserIDsText), [denyUserIDsText]);
  const credentialReady = useMemo(() => {
    const sypaymonAppIDReady = form.sypaymon.app_id.trim() !== '' || Boolean(data?.app_id_configured);
    const sypaymonSecretReady = form.sypaymon.secret.trim() !== '' || Boolean(data?.secret_configured);
    const sypaymonBaseURLReady = form.sypaymon.base_url.trim() !== '' || Boolean(data?.base_url_configured);
    const directAppIDReady = form.alipay_direct_config.app_id.trim() !== '' || Boolean(data?.app_id_configured);
    const directSecretReady = form.alipay_direct_config.secret.trim() !== '' || Boolean(data?.secret_configured);
    const alipayPublicKeyReady = form.alipay_direct_config.alipay_public_key.trim() !== '' || Boolean(data?.alipay_public_key_configured);
    const alipayCertReady = (
      form.alipay_direct_config.alipay_app_cert.trim() !== '' &&
      form.alipay_direct_config.alipay_public_cert.trim() !== '' &&
      form.alipay_direct_config.alipay_root_cert.trim() !== ''
    ) || Boolean(data?.alipay_cert_configured);
    const sypaymonReady = sypaymonAppIDReady && sypaymonSecretReady && sypaymonBaseURLReady;
    const directReady = directAppIDReady && directSecretReady && (alipayPublicKeyReady || alipayCertReady);
    return (!form.sypaymon.enabled || sypaymonReady) && (!form.alipay_direct_config.enabled || directReady);
  }, [data?.alipay_cert_configured, data?.alipay_public_key_configured, data?.app_id_configured, data?.base_url_configured, data?.secret_configured, form.alipay_direct_config.alipay_app_cert, form.alipay_direct_config.alipay_public_cert, form.alipay_direct_config.alipay_public_key, form.alipay_direct_config.alipay_root_cert, form.alipay_direct_config.app_id, form.alipay_direct_config.enabled, form.alipay_direct_config.secret, form.sypaymon.app_id, form.sypaymon.base_url, form.sypaymon.enabled, form.sypaymon.secret]);

  const warnings = useMemo(() => {
    const nextWarnings: string[] = [];
    if (!credentialReady) {
      nextWarnings.push('有已启用的自动打款通道凭据未配置完整，保存后对应通道仍不会真正生效。');
    }
    if (form.sypaymon.enabled && form.default_mode === 'auto' && form.auto_channels.includes('alipay') && form.sypaymon.alipay_product_id <= 0) {
      nextWarnings.push('Sypaymon 支付宝已启用，但支付宝产品 ID 仍为 0。');
    }
    if (form.default_mode === 'auto' && form.auto_channels.includes('bank_card') && form.bank_card_product_id <= 0) {
      nextWarnings.push('银行卡已纳入自动打款通道，但银行卡产品 ID 仍为 0。');
    }
    if (parsedAllowUserIDs.values.length > 0 && parsedDenyUserIDs.values.length > 0) {
      nextWarnings.push('白名单与黑名单同时配置时，建议先确认优先级是否符合运营预期。');
    }
    return nextWarnings;
  }, [credentialReady, form.auto_channels, form.bank_card_product_id, form.default_mode, form.sypaymon.alipay_product_id, form.sypaymon.enabled, parsedAllowUserIDs.values.length, parsedDenyUserIDs.values.length]);

  const save = useCallback(async () => {
    const allowUserIDs = parsedAllowUserIDs.values;
    const denyUserIDs = parsedDenyUserIDs.values;

    if (parsedAllowUserIDs.invalidTokens.length > 0) {
      addToast('error', `白名单用户 ID 仅支持正整数：${parsedAllowUserIDs.invalidTokens.join(', ')}`);
      return false;
    }
    if (parsedDenyUserIDs.invalidTokens.length > 0) {
      addToast('error', `黑名单用户 ID 仅支持正整数：${parsedDenyUserIDs.invalidTokens.join(', ')}`);
      return false;
    }
    if (!form.notify_path.trim().startsWith('/')) {
      addToast('error', '默认回调路径必须以 / 开头，例如 /v1/webhooks/sypaymon/payout');
      return false;
    }
    const baseURL = form.base_url.trim();
    const sypaymonConfig = normalizeProviderConfigForSave(form.sypaymon);
    const alipayDirectConfig = normalizeProviderConfigForSave(form.alipay_direct_config);
    const csjvnpayConfig = normalizeProviderConfigForSave(form.csjvnpay);
    if (!sypaymonConfig.notify_path.startsWith('/') || !alipayDirectConfig.notify_path.startsWith('/')) {
      addToast('error', 'Sypaymon 和支付宝直连的回调路径都必须以 / 开头');
      return false;
    }
    if (!isValidOptionalURL(baseURL) || !isValidOptionalURL(sypaymonConfig.base_url) || !isValidOptionalURL(alipayDirectConfig.base_url)) {
      addToast('error', 'Base URL 必须以 http:// 或 https:// 开头；留空则继续使用服务端环境变量');
      return false;
    }
    const alipayDirectAccounts = normalizeAlipayDirectAccountsForSave(form.alipay_direct_accounts);
    if (alipayDirectAccounts.some((account) => !/^[a-z0-9_-]+$/.test(account.id ?? ''))) {
      addToast('error', '支付宝账号 ID 仅支持小写字母、数字、下划线和短横线');
      return false;
    }
    if (alipayDirectAccounts.some((account) => account.base_url && !isValidOptionalURL(account.base_url))) {
      addToast('error', '支付宝账号 Base URL 必须以 http:// 或 https:// 开头；留空则使用默认直连配置');
      return false;
    }
    if (form.max_auto_amount_fen < 0 || form.retry_limit < 0 || form.query_interval_seconds < 0) {
      addToast('error', '自动打款金额上限、重试次数和查单间隔不能为负数');
      return false;
    }
    if (form.default_mode === 'auto' && form.auto_channels.length === 0) {
      addToast('error', '默认模式为自动时，至少需要勾选一个自动打款通道');
      return false;
    }
    if (form.auto_channels.includes('alipay') && !form.alipay_enabled) {
      addToast('error', '支付宝未启用时，不能加入自动打款通道');
      return false;
    }
    if (form.auto_channels.includes('bank_card') && !form.bank_card_enabled) {
      addToast('error', '银行卡未启用时，不能加入自动打款通道');
      return false;
    }
    const fixedAmounts = normalizeFixedAmountsForSave(form.fixed_amounts);
    const seenAmountFen = new Set<number>();
    for (const amount of fixedAmounts) {
      if (seenAmountFen.has(amount.amount_fen)) {
        addToast('error', `固定提现金额重复：${amount.amount_fen} 分`);
        return false;
      }
      seenAmountFen.add(amount.amount_fen);
      const enabledChannels = amount.channels.filter((channel) => channel.enabled);
      if (amount.enabled && enabledChannels.length !== 1) {
        addToast('error', `金额 ${amount.amount_fen} 分已启用，必须且只能选择一条支付通道`);
        return false;
      }
      if (enabledChannels.some((channel) => channel.provider === 'sypaymon') && (!form.sypaymon.enabled || !form.alipay_enabled)) {
        addToast('error', `金额 ${amount.amount_fen} 分勾选了未启用的 Sypaymon 通道`);
        return false;
      }
      for (const channel of enabledChannels) {
        if (channel.provider !== 'alipay_direct') continue;
        if (!form.alipay_enabled) {
          addToast('error', `金额 ${amount.amount_fen} 分选择了未启用的支付宝通道`);
          return false;
        }
        const accountID = (channel.account_id ?? '').trim().toLowerCase();
        if (!accountID) {
          if (!form.alipay_direct_config.enabled) {
            addToast('error', `金额 ${amount.amount_fen} 分选择了未启用的默认支付宝直连通道`);
            return false;
          }
          continue;
        }
        const account = alipayDirectAccounts.find((item) => item.id === accountID);
        if (!account?.enabled) {
          addToast('error', `金额 ${amount.amount_fen} 分选择了未启用的支付宝账号通道`);
          return false;
        }
      }
    }
    const sypaymonAmounts = providerAmountsFromFixedAmounts(fixedAmounts, 'sypaymon');
    const alipayDirectAmounts = providerAmountsFromFixedAmounts(
      fixedAmounts.map((amount) => ({
        ...amount,
        channels: amount.channels.filter((channel) => channel.provider === 'alipay_direct' && !channel.account_id),
      })),
      'alipay_direct',
    );
    sypaymonConfig.alipay_amounts = sypaymonAmounts;
    alipayDirectConfig.alipay_amounts = alipayDirectAmounts;
    const alipayAmounts = form.alipay_amounts
      .map((item) => ({
        ...item,
        amount_fen: Number(item.amount_fen) || 0,
        label: item.label.trim(),
        sort_order: Number(item.sort_order) || 0,
      }))
      .filter((item) => item.amount_fen > 0);

    setSaving(true);
    try {
      const result = await settingsService.updateWithdrawalPayoutConfig({
        ...form,
        enabled: true,
        auto_review_enabled: form.auto_review_enabled,
        sypaymon: sypaymonConfig,
        alipay_direct_config: alipayDirectConfig,
        alipay_direct_accounts: alipayDirectAccounts,
        csjvnpay: csjvnpayConfig,
        app_id: form.app_id.trim(),
        secret: form.secret.trim(),
        base_url: baseURL,
        alipay_public_key: form.alipay_public_key.trim(),
        alipay_app_cert: form.alipay_app_cert.trim(),
        alipay_public_cert: form.alipay_public_cert.trim(),
        alipay_root_cert: form.alipay_root_cert.trim(),
        alipay_amounts: alipayAmounts,
        fixed_amounts: fixedAmounts,
        notify_path: form.notify_path.trim(),
        allow_user_ids: allowUserIDs,
        deny_user_ids: denyUserIDs,
      });
      setData(result);
      const nextForm = dtoToForm(result);
      setForm(nextForm);
      setAllowUserIDsText(userIDsToText(nextForm.allow_user_ids));
      setDenyUserIDsText(userIDsToText(nextForm.deny_user_ids));
      addToast('success', '自动打款配置已保存，并将在下一次审核或 worker 执行时立即生效');
      return true;
    } catch (e) {
      addToast('error', resolveError(e));
      return false;
    } finally {
      setSaving(false);
    }
  }, [addToast, form, parsedAllowUserIDs.invalidTokens, parsedAllowUserIDs.values, parsedDenyUserIDs.invalidTokens, parsedDenyUserIDs.values]);

  return {
    data,
    form,
    loading,
    saving,
    error,
    allowUserIDsText,
    denyUserIDsText,
    parsedAllowUserIDs,
    parsedDenyUserIDs,
    credentialReady,
    warnings,
    updateField,
    updateProviderField,
    addAlipayDirectAccount,
    updateAlipayDirectAccount,
    removeAlipayDirectAccount,
    setProvider,
    setDefaultMode,
    setChannelEnabled,
    toggleAutoChannel,
    addFixedAmount,
    updateFixedAmount,
    removeFixedAmount,
    setFixedAmountChannel,
    setAllowUserIDsText,
    setDenyUserIDsText,
    save,
    reload: load,
  };
}
